import React, { useState, useEffect, useMemo } from 'react';
import '../AssessmentForm.css';

/**
 * AssessmentTabNetworkEform
 *
 * Handles Assessment section for Network Eform (MSK) reports
 * Maps to NPDetails.eForm table fields
 *
 * Report types: 2010 (Network Eform Initial), 2011 (Network Eform Follow Up)
 */

/**
 * Diagnosis dropdown options
 * Used for both PrimaryDiagnosis and SecondaryDiagnosis fields
 * User should not select the same value for both dropdowns
 */
export const DIAGNOSIS_OPTIONS = [
  { value: 'Acute Psychosis', label: 'Acute Psychosis' },
  { value: 'Acute Stress Reaction', label: 'Acute Stress Reaction' },
  { value: 'Adjustment Disorder', label: 'Adjustment Disorder' },
  { value: 'Alcohol or Sustance use', label: 'Alcohol or Sustance use' },
  { value: 'Anxiety with episodic panic', label: 'Anxiety with episodic panic' },
  { value: 'Bereavement', label: 'Bereavement' },
  { value: 'Bipolar Disorder', label: 'Bipolar Disorder' },
  { value: 'Bulimia Nervosa', label: 'Bulimia Nervosa' },
  { value: 'Chronic Pain Disorder', label: 'Chronic Pain Disorder' },
  { value: 'Depression', label: 'Depression' },
  { value: 'Dysthymia', label: 'Dysthymia' },
  { value: 'Eating Disorder', label: 'Eating Disorder' },
  { value: 'Generalised Anxiety Disorder (GAD)', label: 'Generalised Anxiety Disorder (GAD)' },
  { value: 'Habit or Impulse disorder', label: 'Habit or Impulse disorder' },
  { value: 'Health Anxiety', label: 'Health Anxiety' },
  { value: 'Hypochandrical Disorder', label: 'Hypochandrical Disorder' },
  { value: 'Hypomania', label: 'Hypomania' },
  { value: 'Mixed Anxiety and Depression', label: 'Mixed Anxiety and Depression' },
  { value: 'Nonorganic Insomnia', label: 'Nonorganic Insomnia' },
  { value: 'Obsessive-compulsive Disorder (OCD)', label: 'Obsessive-compulsive Disorder (OCD)' },
  { value: 'Overeating', label: 'Overeating' },
  { value: 'Panic Disorder', label: 'Panic Disorder' },
  { value: 'Panic with agoraphobia', label: 'Panic with agoraphobia' },
  { value: 'Personality Disorder', label: 'Personality Disorder' },
  { value: 'Post-traumatic Stress Disorder (PTSD)', label: 'Post-traumatic Stress Disorder (PTSD)' },
  { value: 'Schizophrenia', label: 'Schizophrenia' },
  { value: 'Social Phobia', label: 'Social Phobia' },
  { value: 'Specific Phobias', label: 'Specific Phobias' },
  { value: 'Other', label: 'Other: please state' }
];

/**
 * CYP-only diagnosis additions (items not already in DIAGNOSIS_OPTIONS).
 * "Depression", "Social Phobia" already exist in the base list.
 * "Generalised Anxiety Disorder", "Obsessive Compulsive Disorder", "Panic"
 * are covered by their bracketed equivalents in the base list.
 */
export const CYP_DIAGNOSIS_ADDITIONS = [
  { value: 'Separation Anxiety', label: 'Separation Anxiety' },
];

/**
 * Maps old CYP diagnosis values (saved without brackets) to the canonical
 * bracketed versions from DIAGNOSIS_OPTIONS so dropdowns display correctly.
 */
const DIAGNOSIS_MIGRATIONS = {
  'Generalised Anxiety Disorder': 'Generalised Anxiety Disorder (GAD)',
  'Obsessive Compulsive Disorder': 'Obsessive-compulsive Disorder (OCD)',
  'Panic': 'Panic Disorder',
};

export const migrateDiagnosisValue = (value) => DIAGNOSIS_MIGRATIONS[value] || value;

/**
 * Convert DateOnly object {Year, Month, Day} to YYYY-MM-DD string format
 * @param {object} dateOnly - DateOnly object with Year, Month, Day properties
 * @returns {string} Date string in YYYY-MM-DD format, or empty string if null
 */
const dateOnlyToString = (dateOnly) => {
  if (!dateOnly || !dateOnly.Year) return '';
  const year = dateOnly.Year;
  const month = String(dateOnly.Month).padStart(2, '0');
  const day = String(dateOnly.Day).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Convert YYYY-MM-DD string to DateOnly object {Year, Month, Day}
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {object|null} DateOnly object or null if empty/invalid
 */
const stringToDateOnly = (dateString) => {
  if (!dateString) return null;
  const parts = dateString.split('-');
  if (parts.length !== 3) return null;
  return {
    Year: parseInt(parts[0], 10),
    Month: parseInt(parts[1], 10),
    Day: parseInt(parts[2], 10)
  };
};

const AssessmentTab = ({ reportId, api, patientInfo, onSave , isPublished}) => {
  // Form state
  const [dateOfInjury, setDateOfInjury] = useState('');
  const [briefHistory, setBriefHistory] = useState('');
  const [previousHistoryDetails, setPreviousHistoryDetails] = useState('');
  const [previousAdminssions, setPreviousAdminssions] = useState('');
  const [previousSecondaryCare, setPreviousSecondaryCare] = useState('');
  const [currentPrescribedMedication, setCurrentPrescribedMedication] = useState('');
  const [isChronicCondition, setIsChronicCondition] = useState(null);
  const [chronicConditionStartDate, setChronicConditionStartDate] = useState('');
  const [primaryDiagnosis, setPrimaryDiagnosis] = useState('');
  const [secondaryDiagnosis, setSecondaryDiagnosis] = useState('');
  const [diagnosisByProfessionalNotes, setDiagnosisByProfessionalNotes] = useState('');
  const [anyOtherRelevantInformation, setAnyOtherRelevantInformation] = useState('');
  const [riskAssessment, setRiskAssessment] = useState('');
  const [additionalSubstanceUse, setAdditionalSubstanceUse] = useState('');
  const [dailyActivities, setDailyActivities] = useState('');
  const [disorderSpecific, setDisorderSpecific] = useState('');
  const [accidentWorkRelated, setAccidentWorkRelated] = useState(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({});

  useEffect(() => {
    loadAssessmentData();
  }, [reportId]);

  const diagnosisOptions = useMemo(() => {
    if (patientInfo?.isCYPApplicable) {
      const combined = [...DIAGNOSIS_OPTIONS, ...CYP_DIAGNOSIS_ADDITIONS];
      return combined.sort((a, b) => a.value.localeCompare(b.value));
    }
    return DIAGNOSIS_OPTIONS;
  }, [patientInfo]);

  const loadAssessmentData = async () => {
    try {
      setLoading(true);
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-assessment-notes`);

      if (response.ok) {
        const data = await response.json();
        setDateOfInjury(dateOnlyToString(data.dateOfInjury));
        setBriefHistory(data.briefHistory || '');
        setPreviousHistoryDetails(data.previousHistoryDetails || '');
        setPreviousAdminssions(data.previousAdminssions || '');
        setPreviousSecondaryCare(data.previousSecondaryCare || '');
        setCurrentPrescribedMedication(data.currentPrescribedMedication || '');
        setIsChronicCondition(data.isChronicCondition);
        setChronicConditionStartDate(dateOnlyToString(data.chronicConditionStartDate));
        setPrimaryDiagnosis(migrateDiagnosisValue(data.primaryDiagnosis || ''));
        setSecondaryDiagnosis(migrateDiagnosisValue(data.secondaryDiagnosis || ''));
        setDiagnosisByProfessionalNotes(data.diagnosisByProfessionalNotes || '');
        setAnyOtherRelevantInformation(data.anyOtherRelevantInformation || '');
        setRiskAssessment(data.riskAssessment || '');
        setAdditionalSubstanceUse(data.additionalSubstanceUse || '');
        setDailyActivities(data.dailyActivities || '');
        setDisorderSpecific(data.disorderSpecific || '');
        setAccidentWorkRelated(data.accidentWorkRelated);

        // Set loaded fields as 'saved' to show green indicator
        const initialSavingStates = {};
        if (data.dateOfInjury) initialSavingStates['DateOfInjury'] = 'saved';
        if (data.briefHistory) initialSavingStates['BriefHistory'] = 'saved';
        if (data.previousHistoryDetails) initialSavingStates['PreviousHistoryDetails'] = 'saved';
        if (data.previousAdminssions) initialSavingStates['PreviousAdminssions'] = 'saved';
        if (data.previousSecondaryCare) initialSavingStates['PreviousSecondaryCare'] = 'saved';
        if (data.currentPrescribedMedication) initialSavingStates['CurrentPrescribedMedication'] = 'saved';
        if (data.isChronicCondition !== null) initialSavingStates['IsChronicCondition'] = 'saved';
        if (data.chronicConditionStartDate) initialSavingStates['ChronicConditionStartDate'] = 'saved';
        if (data.primaryDiagnosis) initialSavingStates['PrimaryDiagnosis'] = 'saved';
        if (data.secondaryDiagnosis) initialSavingStates['SecondaryDiagnosis'] = 'saved';
        if (data.diagnosisByProfessionalNotes) initialSavingStates['DiagnosisByProfessionalNotes'] = 'saved';
        if (data.anyOtherRelevantInformation) initialSavingStates['AnyOtherRelevantInformation'] = 'saved';
        if (data.riskAssessment) initialSavingStates['RiskAssessment'] = 'saved';
        if (data.additionalSubstanceUse) initialSavingStates['AdditionalSubstanceUse'] = 'saved';
        if (data.dailyActivities) initialSavingStates['DailyActivities'] = 'saved';
        if (data.disorderSpecific) initialSavingStates['DisorderSpecific'] = 'saved';
        if (data.accidentWorkRelated !== null) initialSavingStates['AccidentWorkRelated'] = 'saved';
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading assessment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-assessment-notes/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify(value)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    // Field value map for checking if field has content
    const fieldValues = {
      'DateOfInjury': dateOfInjury,
      'BriefHistory': briefHistory,
      'PreviousHistoryDetails': previousHistoryDetails,
      'PreviousAdminssions': previousAdminssions,
      'PreviousSecondaryCare': previousSecondaryCare,
      'CurrentPrescribedMedication': currentPrescribedMedication,
      'IsChronicCondition': isChronicCondition,
      'ChronicConditionStartDate': chronicConditionStartDate,
      'PrimaryDiagnosis': primaryDiagnosis,
      'SecondaryDiagnosis': secondaryDiagnosis,
      'DiagnosisByProfessionalNotes': diagnosisByProfessionalNotes,
      'AnyOtherRelevantInformation': anyOtherRelevantInformation,
      'RiskAssessment': riskAssessment,
      'AdditionalSubstanceUse': additionalSubstanceUse,
      'DailyActivities': dailyActivities,
      'DisorderSpecific': disorderSpecific,
      'AccidentWorkRelated': accidentWorkRelated
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const getCharacterCounter = (currentLength, maxLength) => {
    if (!maxLength) return null;
    const remaining = maxLength - currentLength;
    const isNearLimit = remaining <= 50;
    return (
      <span className={`character-counter ${isNearLimit ? 'near-limit' : ''}`}>
        {currentLength} / {maxLength} characters
      </span>
    );
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    // Track initial character count if not already tracking
    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    // Calculate characters typed since start
    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    // Clear existing timeout
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Trigger save if 50+ characters typed
    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    // Debounce save - 10 seconds after typing stops
    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000);

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    // Clear debounce timeout on blur
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Reset character count for next typing session
    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));

    // Save immediately on blur
    saveField(fieldName, value);
  };

  const handleBooleanChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);
  };

  const handleDateChange = (fieldName, value, setter) => {
    setter(value);
    // Convert string to DateOnly object for API
    const dateOnlyValue = stringToDateOnly(value);
    saveField(fieldName, dateOnlyValue);
  };

  const handleSelectChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading assessment...</p>
      </div>
    );
  }

  // Check if primary and secondary diagnosis are the same (validation warning)
  const hasDuplicateDiagnosis = primaryDiagnosis && secondaryDiagnosis &&
    primaryDiagnosis === secondaryDiagnosis && primaryDiagnosis !== '';

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>

      <div className="form-section">
        <h3>Problem History</h3>

        <div className="form-group">
          <label htmlFor="dateOfInjury">
            Date of injury/onset:
            {getSaveIndicator('DateOfInjury')}
          </label>
          <input
            type="date"
            id="dateOfInjury"
            value={dateOfInjury}
            onChange={(e) => handleDateChange('DateOfInjury', e.target.value, setDateOfInjury)}
            style={{ width: '250px' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="briefHistory">
            Brief description of problem:
            {getSaveIndicator('BriefHistory')}
          </label>
          <textarea
            id="briefHistory"
            value={briefHistory}
            onChange={(e) => handleFieldChange('BriefHistory', e.target.value, setBriefHistory)}
            onBlur={() => handleFieldBlur('BriefHistory', briefHistory)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="previousHistoryDetails">
            History including previous treatment:
            {getSaveIndicator('PreviousHistoryDetails')}
          </label>
          <textarea
            id="previousHistoryDetails"
            value={previousHistoryDetails}
            onChange={(e) => handleFieldChange('PreviousHistoryDetails', e.target.value, setPreviousHistoryDetails)}
            onBlur={() => handleFieldBlur('PreviousHistoryDetails', previousHistoryDetails)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Medical History</h3>

        <div className="form-group">
          <label htmlFor="previousAdminssions">
            Previous admissions to psychiatric or psychological care:
            {getSaveIndicator('PreviousAdminssions')}
          </label>
          <textarea
            id="previousAdminssions"
            value={previousAdminssions}
            onChange={(e) => handleFieldChange('PreviousAdminssions', e.target.value, setPreviousAdminssions)}
            onBlur={() => handleFieldBlur('PreviousAdminssions', previousAdminssions)}
            rows="3"
            maxLength="1000"
            style={{ width: '100%' }}
          />
          {getCharacterCounter(previousAdminssions.length, 1000)}
        </div>

        <div className="form-group">
          <label htmlFor="previousSecondaryCare">
            Previous secondary care:
            {getSaveIndicator('PreviousSecondaryCare')}
          </label>
          <textarea
            id="previousSecondaryCare"
            value={previousSecondaryCare}
            onChange={(e) => handleFieldChange('PreviousSecondaryCare', e.target.value, setPreviousSecondaryCare)}
            onBlur={() => handleFieldBlur('PreviousSecondaryCare', previousSecondaryCare)}
            rows="3"
            maxLength="1000"
            style={{ width: '100%' }}
          />
          {getCharacterCounter(previousSecondaryCare.length, 1000)}
        </div>

        <div className="form-group">
          <label htmlFor="currentPrescribedMedication">
            Current prescribed medication (e.g., psychotropic):
            {getSaveIndicator('CurrentPrescribedMedication')}
          </label>
          <textarea
            id="currentPrescribedMedication"
            value={currentPrescribedMedication}
            onChange={(e) => handleFieldChange('CurrentPrescribedMedication', e.target.value, setCurrentPrescribedMedication)}
            onBlur={() => handleFieldBlur('CurrentPrescribedMedication', currentPrescribedMedication)}
            rows="3"
            maxLength="1000"
            style={{ width: '100%' }}
          />
          {getCharacterCounter(currentPrescribedMedication.length, 1000)}
        </div>

        <div className="form-group">
          <label className="inline-label">
            <strong>Is this a chronic condition?</strong>
            {getSaveIndicator('IsChronicCondition')}
          </label>
          <div className="radio-group-inline">
            <label>
              <input
                type="radio"
                name="isChronicCondition"
                checked={isChronicCondition === true}
                onChange={() => handleBooleanChange('IsChronicCondition', true, setIsChronicCondition)}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="isChronicCondition"
                checked={isChronicCondition === false}
                onChange={() => handleBooleanChange('IsChronicCondition', false, setIsChronicCondition)}
              />
              No
            </label>
          </div>
        </div>

        {isChronicCondition === true && (
          <div className="form-group">
            <label htmlFor="chronicConditionStartDate">
              If so, when did the chronic condition start?
              {getSaveIndicator('ChronicConditionStartDate')}
            </label>
            <input
              type="date"
              id="chronicConditionStartDate"
              value={chronicConditionStartDate}
              onChange={(e) => handleDateChange('ChronicConditionStartDate', e.target.value, setChronicConditionStartDate)}
              style={{ width: '250px' }}
            />
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Diagnosis</h3>

        {hasDuplicateDiagnosis && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            padding: '10px',
            marginBottom: '15px',
            borderRadius: '4px'
          }}>
            <strong>⚠ Warning:</strong> Primary and Secondary Diagnosis should not be the same.
          </div>
        )}

        <div className="form-group">
          <label htmlFor="primaryDiagnosis">
            Primary diagnosis: <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('PrimaryDiagnosis')}
          </label>
          <select
            id="primaryDiagnosis"
            value={primaryDiagnosis}
            onChange={(e) => handleSelectChange('PrimaryDiagnosis', e.target.value, setPrimaryDiagnosis)}
            style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
          >
            <option key="" value="">Select</option>
            {diagnosisOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="secondaryDiagnosis">
            Secondary diagnosis:
            {getSaveIndicator('SecondaryDiagnosis')}
          </label>
          <select
            id="secondaryDiagnosis"
            value={secondaryDiagnosis}
            onChange={(e) => handleSelectChange('SecondaryDiagnosis', e.target.value, setSecondaryDiagnosis)}
            style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
          >
            {diagnosisOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="diagnosisByProfessionalNotes">
            Does the patient have any formal MH diagnosis from a psychiatrist?
            {getSaveIndicator('DiagnosisByProfessionalNotes')}
          </label>
          <textarea
            id="diagnosisByProfessionalNotes"
            value={diagnosisByProfessionalNotes}
            onChange={(e) => handleFieldChange('DiagnosisByProfessionalNotes', e.target.value, setDiagnosisByProfessionalNotes)}
            onBlur={() => handleFieldBlur('DiagnosisByProfessionalNotes', diagnosisByProfessionalNotes)}
            rows="4"
            maxLength="2000"
            style={{ width: '100%' }}
          />
          {getCharacterCounter(diagnosisByProfessionalNotes.length, 2000)}
        </div>

        <div className="form-group">
          <label htmlFor="anyOtherRelevantInformation">
            Any other relevant information:
            {getSaveIndicator('AnyOtherRelevantInformation')}
          </label>
          <textarea
            id="anyOtherRelevantInformation"
            value={anyOtherRelevantInformation}
            onChange={(e) => handleFieldChange('AnyOtherRelevantInformation', e.target.value, setAnyOtherRelevantInformation)}
            onBlur={() => handleFieldBlur('AnyOtherRelevantInformation', anyOtherRelevantInformation)}
            rows="3"
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Risk Assessment & Daily Activities</h3>

        <div className="form-group">
          <label htmlFor="riskAssessment">
            Risk assessment including any actions taken and history of any previous risk: <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('RiskAssessment')}
          </label>
          <textarea
            id="riskAssessment"
            value={riskAssessment}
            onChange={(e) => handleFieldChange('RiskAssessment', e.target.value, setRiskAssessment)}
            onBlur={() => handleFieldBlur('RiskAssessment', riskAssessment)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="additionalSubstanceUse">
            Alcohol and drug/substance use: <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('AdditionalSubstanceUse')}
          </label>
          <textarea
            id="additionalSubstanceUse"
            value={additionalSubstanceUse}
            onChange={(e) => handleFieldChange('AdditionalSubstanceUse', e.target.value, setAdditionalSubstanceUse)}
            onBlur={() => handleFieldBlur('AdditionalSubstanceUse', additionalSubstanceUse)}
            rows="3"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="dailyActivities">
            Daily activities: <span style={{ color: 'red' }}>*</span>
            {getSaveIndicator('DailyActivities')}
          </label>
          <textarea
            id="dailyActivities"
            value={dailyActivities}
            onChange={(e) => handleFieldChange('DailyActivities', e.target.value, setDailyActivities)}
            onBlur={() => handleFieldBlur('DailyActivities', dailyActivities)}
            rows="3"
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Additional Information</h3>

        <div className="form-group">
          <label htmlFor="disorderSpecific">
            Disorder specifics:
            {getSaveIndicator('DisorderSpecific')}
          </label>
          <textarea
            id="disorderSpecific"
            value={disorderSpecific}
            onChange={(e) => handleFieldChange('DisorderSpecific', e.target.value, setDisorderSpecific)}
            onBlur={() => handleFieldBlur('DisorderSpecific', disorderSpecific)}
            rows="3"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label className="inline-label">
            <strong>Is the problem work-related?</strong>
            {getSaveIndicator('AccidentWorkRelated')}
          </label>
          <div className="radio-group-inline">
            <label>
              <input
                type="radio"
                name="accidentWorkRelated"
                checked={accidentWorkRelated === true}
                onChange={() => handleBooleanChange('AccidentWorkRelated', true, setAccidentWorkRelated)}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="accidentWorkRelated"
                checked={accidentWorkRelated === false}
                onChange={() => handleBooleanChange('AccidentWorkRelated', false, setAccidentWorkRelated)}
              />
              No
            </label>
          </div>
        </div>
      </div>
      </fieldset>

    </div>
  );
};

export default AssessmentTab;
